import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  ArrowLeft,
  Save,
  Car,
  Users,
  Package,
  DollarSign,
  Clock,
  AlertTriangle,
  Loader2,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  useVehicleCategory,
  useCreateVehicleCategory,
  useUpdateVehicleCategory,
  useAllVehicleCategories,
} from "@/api-services/hooks/useVehicleCategory";
import {
  VehicleCategoryDTO,
} from "@/api-services/hooks/vehicleCategoryTypes";

const createVehicleCategoryFormSchema = (existingCategories: any[] | null | undefined, currentCategoryId?: number) => z.object({
  vehicleCategoryName: z.string()
    .min(1, "Vehicle category name is required")
    .max(100, "Name must be less than 100 characters")
    .refine((name) => {
      if (!name || name.trim() === '') return false;

      const isDuplicate = existingCategories && Array.isArray(existingCategories) && existingCategories.some((category: any) => {
        const categoryId = category.vehicleCategoryId || category.id;
        return category.vehicleCategoryName?.toLowerCase().trim() === name.toLowerCase().trim() &&
          categoryId !== currentCategoryId;
      });

      return !isDuplicate;
    }, {
      message: "Vehicle category name already exists"
    }),
  vehicleDescription: z.string().min(1, "Description is required").max(500, "Description must be less than 500 characters"),
  maxPassengerCount: z.number().min(1, "Max passenger count must be at least 1").max(20, "Max passenger count cannot exceed 20"),
  maxLuggageVolume: z.number().min(0, "Max luggage volume must be at least 0").max(1000, "Max luggage volume cannot exceed 1000 liters"),
  baseFare: z.number().min(0, "Base fare must be at least 0"),
  ratePerKm: z.number().min(0, "Rate per km must be at least 0"),
  ratePerMinute: z.number().min(0, "Rate per minute must be at least 0"),
  cancellationFee: z.number().min(0, "Cancellation fee must be at least 0"),
  waitTimeLimit: z.number().min(1, "Wait time limit must be at least 1 minute").max(60, "Wait time limit cannot exceed 60 minutes"),
  vehicleIcon: z.string().max(255, "Icon URL must be less than 255 characters").optional(),
  isActive: z.boolean(),
});

type VehicleCategoryFormData = z.infer<ReturnType<typeof createVehicleCategoryFormSchema>>;

const AddEditVehicleCategoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const isEditing = !!categoryId;
  const categoryIdNum = categoryId ? parseInt(categoryId, 10) : 0;

  // Fetch all vehicle categories for validation
  const { data: allCategoriesData } = useAllVehicleCategories({
    onError: (error) => {
      // Silently handle error
    },
  });

  // Memoize categories array to prevent unnecessary re-renders
  // Create a stable reference by using a serialized key
  const categoriesDataString = useMemo(() => {
    if (!Array.isArray(allCategoriesData) || allCategoriesData.length === 0) return '';
    return JSON.stringify(allCategoriesData.map(c => ({
      id: c.vehicleCategoryId || c.id,
      name: c.vehicleCategoryName
    })));
  }, [allCategoriesData]);

  const allCategories = useMemo(() => {
    if (!Array.isArray(allCategoriesData)) return [];
    return allCategoriesData;
  }, [categoriesDataString]);

  // Fetch vehicle category data for edit
  const { data: categoryData, isLoading: loadingCategory } = useVehicleCategory(
    categoryIdNum,
    {
      enabled: isEditing && categoryIdNum > 0,
      onError: (error) => {
        toast.error(`Failed to load vehicle category: ${error.message}`);
      },
    }
  );

  // Create/Update vehicle category hooks
  const { mutate: createVehicleCategory, isPending: isCreating } = useCreateVehicleCategory({
    onSuccess: (data, variables) => {
      toast.success("Vehicle category created successfully!");
      navigate("/admin/vehicle-categories");
    },
    onError: (error, variables) => {
      toast.error(`Failed to create vehicle category: ${error.message}`);
    },
  });

  const { mutate: updateVehicleCategory, isPending: isUpdating } = useUpdateVehicleCategory({
    onSuccess: (data, variables) => {
      toast.success("Vehicle category updated successfully!");
      navigate("/admin/vehicle-categories");
    },
    onError: (error, variables) => {
      toast.error(`Failed to update vehicle category: ${error.message}`);
    },
  });

  // Memoize the form schema to prevent recreation on every render
  // Use categoriesDataString as the dependency to ensure stable reference
  const formSchema = useMemo(() => {
    return createVehicleCategoryFormSchema(allCategories, categoryIdNum);
  }, [categoriesDataString, categoryIdNum]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, isValid, touchedFields },
    reset,
    trigger,
  } = useForm<VehicleCategoryFormData>({
    resolver: zodResolver(formSchema),
    mode: "onBlur", // Only validate on blur, not on mount
    defaultValues: {
      vehicleCategoryName: "",
      vehicleDescription: "",
      maxPassengerCount: 4,
      maxLuggageVolume: 100,
      baseFare: 5.00,
      ratePerKm: 1.50,
      ratePerMinute: 0.30,
      cancellationFee: 2.00,
      waitTimeLimit: 5,
      vehicleIcon: "",
      isActive: true,
    },
  });

  // Revalidate form when categories data changes (only if field has been touched)
  // Use categoriesDataString to avoid unnecessary triggers
  const shouldValidate = useMemo(() => {
    return allCategories.length > 0 && touchedFields.vehicleCategoryName;
  }, [allCategories.length, touchedFields.vehicleCategoryName]);

  useEffect(() => {
    if (shouldValidate && categoriesDataString) {
      trigger("vehicleCategoryName");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesDataString, shouldValidate]);

  // Load data when editing - use a ref to track if we've already loaded
  const hasLoadedData = React.useRef(false);
  const lastCategoryId = React.useRef<number | null>(null);

  // Reset flag when categoryId changes - do this in useEffect, not during render
  useEffect(() => {
    if (lastCategoryId.current !== categoryIdNum) {
      lastCategoryId.current = categoryIdNum;
      hasLoadedData.current = false;
    }
  }, [categoryIdNum]);

  // Load data when editing - only run once per category
  useEffect(() => {
    if (
      categoryData && 
      isEditing && 
      !hasLoadedData.current && 
      categoryData.vehicleCategoryId &&
      categoryData.vehicleCategoryId === categoryIdNum
    ) {
      hasLoadedData.current = true;
      reset({
        vehicleCategoryName: categoryData.vehicleCategoryName || "",
        vehicleDescription: categoryData.vehicleDescription || "",
        maxPassengerCount: categoryData.maxPassengerCount || 4,
        maxLuggageVolume: categoryData.maxLuggageVolume || 100,
        baseFare: categoryData.baseFare || 5.00,
        ratePerKm: categoryData.ratePerKm || 1.50,
        ratePerMinute: categoryData.ratePerMinute || 0.30,
        cancellationFee: categoryData.cancellationFee || 2.00,
        waitTimeLimit: categoryData.waitTimeLimit || 5,
        vehicleIcon: categoryData.vehicleIcon || "",
        isActive: categoryData.isActive ?? true,
      });
    }
    // Only depend on the specific category ID, not the whole categoryData object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryData?.vehicleCategoryId, categoryIdNum, isEditing]);

  const handleCategoryNameBlur = () => {
    trigger("vehicleCategoryName");
  };

  const onSubmit = (data: VehicleCategoryFormData) => {
    if (isEditing) {
      updateVehicleCategory({
        id: categoryIdNum,
        data: data as VehicleCategoryDTO,
      });
    } else {
      createVehicleCategory(data as VehicleCategoryDTO);
    }
  };

  const isLoading = loadingCategory || isCreating || isUpdating;
  const isActive = watch("isActive");

  if (loadingCategory && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading vehicle category...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/vehicle-categories")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {isEditing ? "Edit Vehicle Category" : "Add New Vehicle Category"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditing
                ? "Update vehicle category details and pricing"
                : "Create a new vehicle category with pricing and capacity settings"
              }
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="w-5 h-5" />
                Basic Information
              </CardTitle>
              <CardDescription>
                General details about the vehicle category
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vehicleCategoryName">Category Name *</Label>
                <Input
                  id="vehicleCategoryName"
                  placeholder="e.g., Economy, Premium, SUV"
                  {...register("vehicleCategoryName")}
                  onBlur={handleCategoryNameBlur}
                  className={errors.vehicleCategoryName && touchedFields.vehicleCategoryName ? "border-red-500" : ""}
                />
                {errors.vehicleCategoryName && touchedFields.vehicleCategoryName && (
                  <p className="text-sm text-red-500">{errors.vehicleCategoryName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicleDescription">Description *</Label>
                <Textarea
                  id="vehicleDescription"
                  placeholder="Brief description of the vehicle category"
                  rows={3}
                  {...register("vehicleDescription")}
                />
                {errors.vehicleDescription && (
                  <p className="text-sm text-red-500">{errors.vehicleDescription.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicleIcon">Vehicle Icon URL</Label>
                <Input
                  id="vehicleIcon"
                  placeholder="URL to vehicle icon image"
                  {...register("vehicleIcon")}
                />
                {errors.vehicleIcon && (
                  <p className="text-sm text-red-500">{errors.vehicleIcon.message}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={(checked) => setValue("isActive", checked as boolean)}
                />
                <Label htmlFor="isActive">Category is active</Label>
              </div>
            </CardContent>
          </Card>

          {/* Capacity Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Capacity & Luggage
              </CardTitle>
              <CardDescription>
                Passenger and luggage capacity settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="maxPassengerCount">Max Passengers *</Label>
                <Input
                  id="maxPassengerCount"
                  type="number"
                  min="1"
                  max="20"
                  {...register("maxPassengerCount", { valueAsNumber: true })}
                  className={errors.maxPassengerCount ? "border-red-500" : ""}
                />
                {errors.maxPassengerCount && (
                  <p className="text-sm text-red-500">{errors.maxPassengerCount.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxLuggageVolume">Max Luggage Volume (Liters) *</Label>
                <Input
                  id="maxLuggageVolume"
                  type="number"
                  min="0"
                  max="1000"
                  step="0.1"
                  {...register("maxLuggageVolume", { valueAsNumber: true })}
                  className={errors.maxLuggageVolume ? "border-red-500" : ""}
                />
                {errors.maxLuggageVolume && (
                  <p className="text-sm text-red-500">{errors.maxLuggageVolume.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="waitTimeLimit">Wait Time Limit (Minutes) *</Label>
                <Input
                  id="waitTimeLimit"
                  type="number"
                  min="1"
                  max="60"
                  {...register("waitTimeLimit", { valueAsNumber: true })}
                  className={errors.waitTimeLimit ? "border-red-500" : ""}
                />
                {errors.waitTimeLimit && (
                  <p className="text-sm text-red-500">{errors.waitTimeLimit.message}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pricing Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Pricing Settings
              </CardTitle>
              <CardDescription>
                Default pricing for this vehicle category
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="baseFare">Base Fare ($) *</Label>
                <Input
                  id="baseFare"
                  type="number"
                  min="0"
                  step="0.01"
                  {...register("baseFare", { valueAsNumber: true })}
                  className={errors.baseFare ? "border-red-500" : ""}
                />
                {errors.baseFare && (
                  <p className="text-sm text-red-500">{errors.baseFare.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ratePerKm">Rate per Kilometer ($) *</Label>
                <Input
                  id="ratePerKm"
                  type="number"
                  min="0"
                  step="0.01"
                  {...register("ratePerKm", { valueAsNumber: true })}
                  className={errors.ratePerKm ? "border-red-500" : ""}
                />
                {errors.ratePerKm && (
                  <p className="text-sm text-red-500">{errors.ratePerKm.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ratePerMinute">Rate per Minute ($) *</Label>
                <Input
                  id="ratePerMinute"
                  type="number"
                  min="0"
                  step="0.01"
                  {...register("ratePerMinute", { valueAsNumber: true })}
                  className={errors.ratePerMinute ? "border-red-500" : ""}
                />
                {errors.ratePerMinute && (
                  <p className="text-sm text-red-500">{errors.ratePerMinute.message}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Additional Fees */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Additional Fees
              </CardTitle>
              <CardDescription>
                Cancellation and other additional fees
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cancellationFee">Cancellation Fee ($) *</Label>
                <Input
                  id="cancellationFee"
                  type="number"
                  min="0"
                  step="0.01"
                  {...register("cancellationFee", { valueAsNumber: true })}
                  className={errors.cancellationFee ? "border-red-500" : ""}
                />
                {errors.cancellationFee && (
                  <p className="text-sm text-red-500">{errors.cancellationFee.message}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/vehicle-categories")}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="bg-gradient-primary hover:opacity-90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditing ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Update Category" : "Create Category"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditVehicleCategoryPage;